import {ID} from '@wandb/weave/common/util/id';
import {type Element, type Node as SlateNode} from 'slate';

import {panelViews} from '../PanelBank/types';
import {type ArtifactVersionMetricPanelConfig, INITIAL_CONFIG} from './types';

export const ARTIFACT_VERSIONS_GRID_TYPE = 'artifact-versions-grid';

export type ArtifactVersionMetricGridPanelConfig = Omit<
  ArtifactVersionMetricPanelConfig,
  'membershipIds'
>;

export interface ArtifactVersionMetricGridPanel {
  __id__: string;
  viewType: typeof panelViews.artifactVersionMetrics;
  config: ArtifactVersionMetricGridPanelConfig;
  isAuto: false;
}

export interface ArtifactVersionsGridMetadata {
  /** Assigned on insert; backfilled by Slate normalize for older cards. */
  gridId?: string;
  membershipIds: string[];
  panels: ArtifactVersionMetricGridPanel[];
}

export interface ArtifactVersionsGrid extends Element {
  type: typeof ARTIFACT_VERSIONS_GRID_TYPE;
  metadata: ArtifactVersionsGridMetadata;
}

export const isArtifactVersionsGrid = (
  node: SlateNode
): node is ArtifactVersionsGrid => node.type === ARTIFACT_VERSIONS_GRID_TYPE;

export function withoutMembershipIds(
  configWithMembershipIds: ArtifactVersionMetricPanelConfig
): ArtifactVersionMetricGridPanelConfig {
  const {membershipIds: _membershipIds, ...config} = configWithMembershipIds;
  return config;
}

export function createArtifactVersionMetricsPanel(
  configOverrides: Partial<ArtifactVersionMetricGridPanelConfig> = {}
): ArtifactVersionMetricGridPanel {
  return {
    __id__: ID(),
    viewType: panelViews.artifactVersionMetrics,
    config: {
      ...withoutMembershipIds(INITIAL_CONFIG),
      ...configOverrides,
    },
    isAuto: false,
  };
}

const newlyCreatedArtifactVersionsGridIds = new Set<string>();

export function createArtifactVersionsGridMetadata(): ArtifactVersionsGridMetadata {
  const gridId = ID();
  // `createArtifactVersionsGridMetadata` is called by the shared Slate slash-menu
  // path. Keep the new-grid signal local to artifact metrics instead of adding
  // collection-overview tracking concerns to Slate/Reports code, and avoid
  // persisting analytics-only state in the overview spec.
  newlyCreatedArtifactVersionsGridIds.add(gridId);

  return {
    gridId,
    membershipIds: [],
    panels: [createArtifactVersionMetricsPanel()],
  };
}

export function consumeNewlyCreatedArtifactVersionsGridId(
  gridId?: string
): boolean {
  if (gridId == null || !newlyCreatedArtifactVersionsGridIds.has(gridId)) {
    return false;
  }

  newlyCreatedArtifactVersionsGridIds.delete(gridId);
  return true;
}
